import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronDown, Search, X } from "lucide-react";

/** Search-box multi-select combobox.
 *
 * Props:
 *   value       Array of selected IDs
 *   onChange    (nextIdsArray) => void
 *   options     Array<{ id, name }> (rename via `valueKey`, `labelKey`)
 *   placeholder String
 *   maxHeight   px, default 260
 *   testid      Base data-testid; produces {tid}-btn, {tid}-search, {tid}-opt-{id}
 */
export default function MultiSelect({
  value = [], onChange, options = [],
  valueKey = "id", labelKey = "name",
  placeholder = "Search…",
  maxHeight = 260,
  testid = "multi",
  chipsLabel = "selected",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = useMemo(() => new Set(value || []), [value]);
  const filtered = useMemo(() => {
    if (!q) return options;
    const s = q.toLowerCase();
    return options.filter(o => (o[labelKey] || "").toLowerCase().includes(s));
  }, [options, q, labelKey]);

  function toggle(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  }

  function clearAll() { onChange([]); setQ(""); }

  const selectedItems = options.filter(o => selected.has(o[valueKey]));

  return (
    <div className="relative" data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full min-h-10 px-3 py-1.5 rounded-md border border-slate-200 bg-white flex items-center gap-2 flex-wrap text-left hover:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
        data-testid={`${testid}-btn`}
      >
        {selectedItems.length === 0 && (
          <span className="text-slate-400 text-sm flex-1">{placeholder}</span>
        )}
        {selectedItems.map((o) => (
          <Badge key={o[valueKey]} variant="secondary" className="bg-sky-100 text-sky-700 gap-1" data-testid={`${testid}-chip-${o[valueKey]}`}>
            {o[labelKey]}
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); toggle(o[valueKey]); }}
              className="ml-1 cursor-pointer inline-flex items-center"
            >
              <X className="h-3 w-3"/>
            </span>
          </Badge>
        ))}
        <ChevronDown className={`h-4 w-4 text-slate-400 ml-auto transition ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <>
          {/* click-outside overlay */}
          <button type="button" className="fixed inset-0 z-40 cursor-default" onClick={() => setOpen(false)} aria-label="close"/>
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden" data-testid={`${testid}-panel`}>
            <div className="relative border-b border-slate-100">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="pl-9 border-0 focus-visible:ring-0" autoFocus data-testid={`${testid}-search`}/>
              {selectedItems.length > 0 && (
                <button type="button" onClick={clearAll} className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-slate-500 hover:text-red-600 font-bold" data-testid={`${testid}-clear`}>
                  Clear all
                </button>
              )}
            </div>
            <div className="overflow-y-auto" style={{ maxHeight }}>
              {filtered.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-slate-400">No matches.</div>
              )}
              {filtered.map((o) => {
                const isSel = selected.has(o[valueKey]);
                return (
                  <button
                    key={o[valueKey]}
                    type="button"
                    onClick={() => toggle(o[valueKey])}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-sky-50 ${isSel ? "bg-sky-50/40" : ""}`}
                    data-testid={`${testid}-opt-${o[valueKey]}`}
                  >
                    <div className={`h-4 w-4 rounded border-2 grid place-items-center flex-shrink-0 ${isSel ? "bg-sky-500 border-sky-500 text-white" : "border-slate-300"}`}>
                      {isSel && <Check className="h-3 w-3"/>}
                    </div>
                    <span className="flex-1">{o[labelKey]}</span>
                  </button>
                );
              })}
            </div>
            {selectedItems.length > 0 && (
              <div className="border-t border-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                {selectedItems.length} {chipsLabel}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
